import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import MetricsEntryPoint, {
	type MetricsEntryPointProps,
} from '@databox/ember-integrations/lib/components/Metrics/MetricsEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import LineageExplorerHostComponent, {
	type OpenLineageExplorer,
} from 'webapp/components/lineage-explorer-host';

interface MetricsPageSignature {
	Element: HTMLDivElement;
}

export default class MetricsPageComponent extends Component<MetricsPageSignature> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-registry') declare metricRegistryService: Service.MetricRegistry;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	private openLineage: OpenLineageExplorer | null = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderMetrics());
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	captureLineageOpen(open: OpenLineageExplorer): void {
		this.openLineage = open;
	}

	// Built once: React keeps the host as a context value and callback dependency.
	@cached
	get host(): MetricsEntryPointProps['host'] {
		return this.metricRegistryService.createHost((metricKey) => {
			this.openLineage?.({ target: { metricKey } });
		});
	}

	get entryPointProps(): MetricsEntryPointProps {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		return {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken ?? '',
			dataToken: dataToken ?? '',
			spaceId: this.accountService.id,
			user: {
				id: this.currentUserService.numericId,
				name: this.currentUserService.displayName,
			},
			host: this.host,
			isDemo: this.currentUserService.isDemo,
			// Same gate as Home's AI section.
			isAiEnabled: this.aiChatService.isAiChatAvailable,
			// Same gate as every other Add to Home: no Home for an agency user in a client space.
			hasHome: this.currentUserService.hasHome,
			// /m/metrics: the native app gets the read-only list, with search and View only.
			isMobileApp: this.navigationService.isMobileApp,
		};
	}

	private renderMetrics() {
		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				MetricsEntryPoint(this.entryPointProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="metrics-page-container"
			...attributes
		/>
		{{! The mobile page has no Lineage explorer action. }}
		{{#unless this.navigationService.isMobileApp}}
			<LineageExplorerHostComponent @onReady={{this.captureLineageOpen}} />
		{{/unless}}
	</template>
}
